/**
 * The body of the gateway's `GET /status` (ADR-078 decision 1), shared so the gateway writes and the page reads
 * one shape: the server's state and its maintenance schedule, in milliseconds relative to the gateway's clock
 * when it answered, never an epoch, so a page's wrong clock cannot move a deadline. The page fixes each deadline
 * at receipt.
 */

/**
 * The path the gateway answers the status at, read whole.
 */
export const STATUS_PATH = "/status";

/**
 * The server's state: open to logins, busy (a login now would wait in the login queue, ADR-127 decision 8), or
 * closed for maintenance.
 */
export type ServerState = "open" | "busy" | "maintenance";

/**
 * A maintenance schedule as the status carries it: how long until it starts (0 once it has) and until its
 * planned end (negative once past it, null when no end was given).
 */
export interface StatusMaintenance {
  readonly startsInMs: number;
  readonly endsInMs: number | null;
}

/**
 * The status body. `worldVersion` is the world version the gateway's map server runs (ADR-050's amendment of
 * 2026-10-02), which a page loads in place of the CDN's pointer; a gateway before it sends none. `accountMail`
 * says whether the gateway sends account mail (ADR-092 decision 6), which is what lets a page offer a reset by
 * mail and a confirmation link; a gateway before it sends none, which a page reads as no. `waitMs` is the estimated
 * wait of a player arriving at a busy server (ADR-127 decision 8), in milliseconds and in coarse steps of minutes; a gateway with
 * too little data to estimate from, and one before it, send none. The body carries no count and no place.
 */
export interface StatusBody {
  readonly state: ServerState;
  readonly maintenance: StatusMaintenance | null;
  readonly worldVersion?: string;
  readonly accountMail?: boolean;
  readonly waitMs?: number;
}

const WORLD_VERSION = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/**
 * Whether a text is a world version name a pointer may carry, and so one a page may put in a path: a letter or
 * digit, then up to 63 letters, digits, dots, underscores or hyphens.
 *
 * @param value The text.
 * @returns True for a safe version name.
 */
export function isWorldVersion(value: unknown): value is string {
  return typeof value === "string" && WORLD_VERSION.test(value);
}

const STATES: ReadonlySet<string> = new Set<ServerState>(["open", "busy", "maintenance"]);

/**
 * Whether a value is a whole number of milliseconds a double holds exactly.
 *
 * @param value The value.
 * @returns True for a safe integer.
 */
function wholeMs(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value);
}

/**
 * Reads a maintenance schedule.
 *
 * @param value The parsed `maintenance` field.
 * @returns The schedule, null for none, or undefined when it cannot be read.
 */
function readMaintenance(value: unknown): StatusMaintenance | null | undefined {
  if (value === null) {
    return null;
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const { startsInMs, endsInMs } = value as Record<string, unknown>;
  if (!wholeMs(startsInMs) || startsInMs < 0 || (endsInMs !== null && !wholeMs(endsInMs))) {
    return undefined;
  }
  return { startsInMs, endsInMs };
}

/**
 * Reads a parsed status body. Fields a later gateway adds are ignored; a state this page does not know, a
 * missing field or a number out of shape makes the whole body unreadable, which the page treats as no news
 * rather than as offline (ADR-078 decision 2). A body that says maintenance runs must carry its schedule. The world
 * version is kept only when it is a safe version name (`isWorldVersion`); one that is not is left out and the rest
 * of the body still reads. `accountMail` is kept only when it is a boolean, and `waitMs` only when it is a whole
 * number of milliseconds from 0, in the same way.
 *
 * @param value The parsed JSON.
 * @returns The body, or null when it cannot be read.
 * @example
 * readStatusBody({ state: "open", maintenance: { startsInMs: 60000, endsInMs: null } });
 */
export function readStatusBody(value: unknown): StatusBody | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const { state, maintenance, worldVersion, accountMail, waitMs } = value as Record<string, unknown>;
  if (typeof state !== "string" || !STATES.has(state)) {
    return null;
  }
  const schedule = readMaintenance(maintenance);
  if (schedule === undefined || (state === "maintenance" && schedule === null)) {
    return null;
  }
  return {
    state: state as ServerState,
    maintenance: schedule,
    ...(isWorldVersion(worldVersion) ? { worldVersion } : {}),
    ...(typeof accountMail === "boolean" ? { accountMail } : {}),
    ...(wholeMs(waitMs) && waitMs >= 0 ? { waitMs } : {}),
  };
}
